import React, { useState } from 'react'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { Collateral, CollateralPrice, Product, Severity, UserPreference, ValidationIssue } from 'api'
import { DialogField, DialogRow } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Locale, Translation } from 'locale'
import styled from '@emotion/styled'
import { Nullable } from 'utils'
import { CollateralMainDetailsPanel } from './CollateralMainDetailsPanel'
import { Tabs } from 'view/components/tabs'
import { CollateralAdditionalDetailsPanel } from './CollateralAdditionalDetailsPanel'
import { CollateralDialogFields } from './collateralDialogUtils'
import { useSubscribeToCollateralDetails } from './useSubscribeToCollateralDetails'
import { ValidationTooltip } from 'view/components/tooltip'
import { useOnMount } from 'view/hooks'

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;
  width: 700px;
  height: 650px;
  overflow-y: scroll;
`

export type ReadonlyFields = { [key in CollateralDialogFields]: boolean }

interface CollateralDialogComponentProps {
  title: string
  originalCollateral: Nullable<Collateral>
  collateral: Collateral
  collateralPrice: CollateralPrice
  collateralPriceValidationIssues: ValidationIssue[]
  validationIssues: ValidationIssue[]
  readOnly: ReadonlyFields
  userPreference: UserPreference
  onUpdateCollateralPrice: (cleanPrice: number, collateralId?: string) => void
  onUpdateCollateral: (partialCollateral: Partial<Collateral>) => void
  product: Nullable<Product>
  onSave: () => void
  onClose: () => void
}

export const CollateralDialogComponent: React.FC<CollateralDialogComponentProps> = ({
  originalCollateral,
  collateral,
  collateralPrice,
  product,
  readOnly,
  title,
  collateralPriceValidationIssues,
  validationIssues,
  onClose,
  onSave,
  onUpdateCollateral,
  onUpdateCollateralPrice,
  userPreference
}) => {
  const [selectedTab, setSelectedTab] = useState<Translation>(Translation.TC_MAIN_DETAILS)
  const { subscribeToCollateralDetails, unsubscribeFromCollateralDetails } = useSubscribeToCollateralDetails()

  useOnMount(() => {
    subscribeToCollateralDetails(collateral.id)
  })

  const handleClose = () => {
    unsubscribeFromCollateralDetails(collateral.id)
    onClose()
  }

  const handleSave = () => {
    unsubscribeFromCollateralDetails(collateral.id)
    onSave()
  }

  const handleChangeTab = (tab: Translation) => {
    setSelectedTab(tab)
  }

  return (
    <ModalWindow title={title}>
      <Tabs
        selectedTab={selectedTab}
        onChangeTab={handleChangeTab}
        tabs={[Translation.TC_MAIN_DETAILS, Translation.TC_ADDITIONAL_DETAILS]}
      />
      <Content>
        {selectedTab === Translation.TC_MAIN_DETAILS && (
          <CollateralMainDetailsPanel
            originalCollateral={originalCollateral}
            collateral={collateral}
            collateralPrice={collateralPrice}
            product={product}
            userPreference={userPreference}
            readOnly={readOnly}
            validationIssues={validationIssues}
            collateralPriceValidationIssues={collateralPriceValidationIssues}
            onUpdateCollateral={onUpdateCollateral}
            onUpdateCollateralPrice={onUpdateCollateralPrice}
          />
        )}
        {selectedTab === Translation.TC_ADDITIONAL_DETAILS && (
          <CollateralAdditionalDetailsPanel collateral={collateral} readOnly={readOnly} validationIssues={validationIssues} />
        )}
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'CollateralDialog-TextInputField-CancelButton'} type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <ValidationTooltip showField validationIssues={validationIssues.filter(v => v.severity === Severity.ERROR)}>
            <DialogField data-test-id="CollateralDialog-SaveButtonWrapper" flex={0}>
              <TextButton
                dataTestId={'CollateralDialog-TextInputField-SaveButton'}
                type={TextButtonType.Primary}
                disabled={readOnly.save}
                onClick={handleSave}>
                {Locale.translate(Translation.TC_BUTTON_SAVE)}
              </TextButton>
            </DialogField>
          </ValidationTooltip>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
